/**
 * API client for the workflows service (`lara-data-plane/src/workflows`).
 *
 * Routes through the gateway: /api/v1/gateway/workflows/{path}.
 *
 * **Versioning note:** the gateway prefix uses /api/v1/ (the gateway's own
 * version), but our service exposes /api/v2/ internally — agentic owns
 * /api/v1/workflows for legacy workflow-templates. See
 * src/workflows/docs/dsl-design.md for the v2 decision.
 *
 * Endpoints:
 *   POST   /workflows/execute                       — run a workflow
 *   POST   /workflows/executions/{id}/resume        — resume by id
 *   GET    /workflows/executions/{id}               — fetch latest state
 *   GET    /workflows/streams                       — catalog
 *   GET    /workflows/streams/envelope-schema       — JSON schema (v1)
 *   GET    /workflows/streams/depth                 — XLEN snapshot
 *   POST   /workflows/subscriptions                 — create
 *   GET    /workflows/subscriptions                 — list (org-scoped)
 *   GET    /workflows/subscriptions/{id}            — read one
 *   PATCH  /workflows/subscriptions/{id}            — update
 *   DELETE /workflows/subscriptions/{id}            — delete
 *   GET    /workflows/definitions/grid              — definitions grid (paged/searched/sorted)
 *   GET    /workflows/definitions/keys              — (key, name, enabled) per key, for filters
 *   GET    /workflows/projects/grid                 — projects grid (paged/searched/sorted)
 *   PUT    /workflows/projects/{id}/chain           — run the project's workflows one after another
 *   GET    /workflows/projects/{id}/chain/runs      — chain links + a page of chain runs
 *   POST   /workflows/definitions/key/{key}/triggers/after-workflow — start it after other workflows
 *   GET    …/business2cloud/api/v1/retry-failures/grid — retry-failures grid + facets
 */

import { portalFetch, resolveBaseUrl } from "@/lib/portalApi";
import type { DslWorkflow } from "@/components/portal/workflow-flow-v2/types";
import type { ConfigFieldSchema } from "@/components/portal/workflow-flow-v2/workflowConfigHelpers";

const API_BASE_PATH = `${resolveBaseUrl()}/api/v1/gateway/workflows/api/v2`;

/** One authoritative DSL validation issue, path-pinned into the workflow JSON —
 * the shape POST /workflows/dsl/generate (and /dsl/validate) returns. */
export interface DslValidationIssue {
  type: string;
  loc: (string | number)[];
  msg: string;
}

/** Response from POST /workflows/dsl/generate (WS2 "describe it → workflow").
 * `ok` means `dsl` passed the authoritative Workflow.model_validate gate and is
 * safe to drop on the canvas. On failure `dsl` is the last candidate and `errors`
 * the path-pinned issues (same shape as /dsl/validate). */
export interface GenerateDslResponse {
  ok: boolean;
  dsl: DslWorkflow | null;
  errors: DslValidationIssue[];
  attempts: number;
  reasoning?: string | null;
  assumptions: string[];
  confidence?: number | null;
  /** Stable id for this generation. Stamped into the applied workflow's config
   * so a later successful run can be traced back (WS2 run-side metric). */
  generation_id: string;
}

/** One MyApp table column, as returned by GET /workflows/forms/{id}/fields.
 * Only the fields the WS3c data-mapper reads are typed; the endpoint returns
 * the full TableFieldResponse. */
export interface MyAppFieldDef {
  column_name: string;
  name?: string | null;
  is_required?: boolean;
  field_type?: { code?: string | null } | null;
}

/** One org secret's metadata (WS5). The value is write-only — the API never
 * returns it, so there is no value field here. */
export interface SecretName {
  name: string;
  description: string | null;
  created_at_utc?: string | null;
}

/** Server-side paginated secrets list — {items, total} per the grid convention. */
export interface SecretListResponse {
  items: SecretName[];
  total: number;
}

export interface ListSecretsOptions {
  page?: number;
  page_size?: number;
  search?: string;
  sort?: "name" | "created_at";
  order?: "asc" | "desc";
}

/** Connector schema shape from GET /workflows/connectors/{key}/schema (WS3c). */
export interface ConnectorSchemaProperty {
  name: string;
  type?: string | null;
  nullable?: boolean;
  primary?: boolean;
  length?: number | null;
}
export interface ConnectorSchemaObject {
  name: string;
  properties: ConnectorSchemaProperty[];
}
export interface ConnectorSchema {
  connector_key: string;
  direction: string;
  connector_type?: string | null;
  kind: string; // "sql" | "api"
  schema: string | null;
  objects: ConnectorSchemaObject[];
  truncated: boolean;
}

/** Request body for the read-only query-preview endpoint (WS3b). Mirrors
 * ``QueryPreviewRequest`` on the workflows side. ``schema`` fills the
 * ``{schema}`` token; ``params`` bind the literal ``@name`` placeholders the
 * author has typed (the preview does NOT run the node's ``${...}`` value
 * interpolation — those reference live execution state). */
export interface QueryPreviewRequest {
  query: string;
  direction?: string;
  schema?: string | null;
  params?: Record<string, unknown>;
  limit?: number;
}
export interface QueryPreviewResult {
  rows: Record<string, unknown>[];
  row_count: number;
  columns: string[];
  truncated: boolean;
}

/** Retry failures live in business2cloud's ``dp_business2cloud.RetryFailure``
 * table because b2c owns that schema, but conceptually they are workflow
 * execution failures (the 4 out-of-process node primitives write here when
 * their retry budget is exhausted). Surface them under /portal/workflows. */
const B2C_BASE_PATH = `${resolveBaseUrl()}/api/v1/gateway/business2cloud/api/v1`;

// ---------------------------------------------------------------------------
// Shared types — mirror the FastAPI Pydantic models on the workflows side
// ---------------------------------------------------------------------------

/** `completed_with_errors`: the walk reached End, but at least one node took a
 * failure exit (`failed` / `*_failed`) on the way. `error` names the first:
 * "<node_id> took <exit>: <text>". */
export type ExecutionStatus =
  | "completed"
  | "completed_with_errors"
  | "failed"
  | "suspended";

/** Server response from execute / resume / GET. */
export interface ExecutionResponse {
  /**
   * Persisted execution id. Always set in production; absent only when the
   * service is running without MSSQL (e.g. local unit-test container).
   */
  execution_id: string | null;
  status: ExecutionStatus;
  steps_taken: number;
  node_outputs: Record<string, Record<string, unknown>>;
  final_node_id: string | null;
  final_exit: string | null;
  error: string | null;

  // Suspend-only metadata — non-load-bearing for resume (the caller now uses
  // execution_id) but kept so the UI can surface what's pending.
  suspended_node_id: string | null;
  suspend_token: string | null;
  suspend_reason: string | null;
  trigger_snapshot: Record<string, unknown> | null;
}

export interface ExecuteWorkflowRequest {
  workflow: Record<string, unknown>;
  trigger_id: string;
  payload?: Record<string, unknown>;
  trigger_envelope?: Record<string, unknown>;
  /** Dry-run / test-run (WS6). External nodes are simulated (no side effects),
   * control-flow runs for real, and nothing is persisted. */
  dry_run?: boolean;
  /** Fidelity layer: run reads / the agent for real, still faking writes.
   * Only meaningful with dry_run. */
  live_reads?: boolean;
  /**
   * Block until the walk finishes and return the full result inline. The API
   * defaults to async (202 + a `running` execution_id to poll), but this admin
   * console renders the result synchronously, so it opts in. Omit for
   * fire-and-forget.
   */
  wait?: boolean;
}

export interface ResumeExecutionRequest {
  decision: string;
  decision_output?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Executions list (GET /workflows/executions) — recent runs for the org,
// with bucket counts. Surfaces failures so users can spot a broken
// subscription without opening MSSQL.
// ---------------------------------------------------------------------------

export type ExecutionListStatus =
  | "completed"
  | "completed_with_errors"
  | "failed"
  | "running"
  | "suspended";

/** Server-sortable columns on the executions list — mirrors the backend
 *  `ExecutionDAO.SORT_COLUMNS` allow-list. */
export type ExecutionSortField =
  | "started_at"
  | "completed_at"
  | "status"
  | "workflow_key"
  | "project_name";

export interface ExecutionSummary {
  id: string;
  organisation_id: string;
  trigger_id: string | null;
  trigger_event_id: string | null;
  status: ExecutionListStatus;
  started_at_utc: string | null;
  completed_at_utc: string | null;
  error_message: string | null;
  suspended_node_id: string | null;
  // Derived (LEFT-joined) on the backend — the workflow this run executed and
  // the project it belongs to. Null for inline-DSL runs / unassigned workflows.
  workflow_key: string | null;
  project_id: string | null;
  project_name: string | null;
}

export interface WorkflowLastRun {
  workflow_key: string;
  last_execution_id: string | null;
  last_started_at_utc: string | null;
  last_status: ExecutionListStatus | null;
  has_schedule: boolean;
  /** True when a scheduled workflow has run before but is now stale vs its
   *  cadence. Manual/event workflows + never-run are never overdue. */
  is_overdue: boolean;
}

export interface LastRunsResponse {
  rows: WorkflowLastRun[];
}

export interface ExecutionListResponse {
  rows: ExecutionSummary[];
  limit: number;
  offset: number;
}

/** ``GET /workflows/executions/counts`` — one entry per status SEEN; callers
 *  default a missing status to 0. Split out of the list so a page turn or a
 *  sort never recounts the org's whole history (Supernova Check List row 19). */
export interface ExecutionCountsResponse {
  counts_by_status: Partial<Record<ExecutionListStatus, number>>;
}

// ---------------------------------------------------------------------------
// Stream catalog
// ---------------------------------------------------------------------------

export interface StreamCatalogEntry {
  stream: string;
  source: string;
  record_type: string;
  description: string;
  event_types: string[];
}

// ---------------------------------------------------------------------------
// Picker registry — shared shape across every ref_type
// ---------------------------------------------------------------------------

/** One row in a picker dropdown — same shape regardless of ref_type. */
export interface PickerItem {
  /** Underlying resource id (form UUID, conversation id, stream key, …). */
  id: string;
  /** Human-readable name shown in the dropdown row. */
  label: string;
  /** Optional per-org shortname. Present when the org has aliased this resource. */
  alias: string | null;
  /** The value the canvas writes into the DSL when this item is picked. */
  expression: string;
  /** Optional one-line context (subtitle under the label). */
  description: string | null;
  /** Ref-type-specific extras (e.g. for streams: source/record_type/event_types). */
  metadata: Record<string, unknown>;
}

export interface PickerResponse {
  ref_type: string;
  items: PickerItem[];
}

export interface StreamCatalogResponse {
  schema_version: number;
  streams: StreamCatalogEntry[];
}

export interface StreamDepthEntry {
  stream: string;
  source: string;
  depth: number;
  max_length: number;
  ratio: number;
  observed_at_unix: number;
  error: string | null;
}

export interface StreamDepthResponse {
  last_poll_unix: number | null;
  last_poll_error: string | null;
  streams: StreamDepthEntry[];
}

// ---------------------------------------------------------------------------
// Subscriptions
// ---------------------------------------------------------------------------

export interface SubscriptionResponse {
  id: string;
  organisation_id: string;
  name: string;
  stream: string;
  trigger_id: string;
  workflow: Record<string, unknown>;
  filter: Record<string, unknown> | null;
  /**
   * FK to the ``WorkflowDefinition`` row this subscription runs.
   * Always populated post-#70-slice-2 (slice 1 backfilled every
   * existing subscription); the field is left optional in TS purely
   * so older fixtures keep type-checking.
   */
  definition_id?: string | null;
  is_active: boolean;
}

/**
 * Body for ``PATCH /workflows/definitions/key/{workflow_key}/triggers/{trigger_id}/active``.
 *
 * Per-trigger pause/resume. Each trigger in a workflow's DSL has a
 * backing ``WorkflowSubscription`` row created by the publish-time
 * reconciler; this flips that row's ``IsActive`` flag without
 * disturbing the workflow's other triggers.
 */
export interface SetTriggerActiveRequest {
  is_active: boolean;
}

export interface TriggerActiveResponse {
  workflow_key: string;
  trigger_id: string;
  is_active: boolean;
}

// ---------------------------------------------------------------------------
// Workflow definitions — authored DSL store. One row per
// (WorkflowKey, Version); subscriptions FK into a frozen version.
// ---------------------------------------------------------------------------

/** Backend-computed plain-English rendering of a workflow's graph. Mirrors
 * `DefinitionSummary` in lara-data-plane's services/dsl_summary.py. */
/** One step, as prose the author wrote where they wrote any.
 *
 * `source` says which rung of the fallback produced `line`: the author's
 * `$comment`, a clause derived from the node's own config, or — last resort —
 * the node kind's generic docstring. Surfaced so a regression to generic text
 * is visible rather than looking like success. */
export interface StepSummary {
  line: string;
  /** The author's full comment, where there was one. `line` is its first
   * sentence; this is everything, for the expandable detail. */
  detail?: string | null;
  node_id?: string | null;
  source?: "comment" | "structure" | "kind";
}

export interface DefinitionSummary {
  /** One-line "Every 4h → 6 steps → sn-control.PurchaseOrderControlTable". */
  headline: string;
  /** What starts it: "Every 4h", "Run manually", "After xero_rangel syncs". */
  trigger: string;
  /** Author intent — WHY it exists. The typed `description` if there is one,
   * else the workflow-level `$comment`. Absent on an older data-plane. */
  intent?: string | null;
  intent_full?: string | null;
  /** One sentence per node, in execution order.
   *
   * KEPT AS PLAIN STRINGS DELIBERATELY: this array is rendered directly into
   * JSX by deployed builds, and an object here throws "Objects are not valid as
   * a React child" and takes the route down. The rich form is `step_lines`;
   * this mirrors it. */
  steps: string[];
  step_lines?: StepSummary[];
  /** What it reaches — tables for SQL nodes (their connector is a database
   * handle, not the system touched), connector names for genuinely external
   * ones, raw keys when b2c was unreachable. */
  touches: string[];
  node_count: number;
}

export interface DefinitionResponse {
  id: string;
  organisation_id: string;
  workflow_key: string;
  version: number;
  name: string;
  workflow: Record<string, unknown>;
  /** True if this row is the currently-firing version for its
   * (org, workflow_key). Migrations guarantee at most one ``true`` per
   * group; the Definitions table renders one row per WorkflowKey and
   * the active row's flags drive the Switch state. */
  /**
   * Set ONLY by the publish endpoints (create / new version). Declaring a
   * trigger in the DSL IS the subscription, so publishing reconciles them —
   * and a trigger the reconciler REFUSED used to be visible only in service
   * logs, leaving a workflow that looked published with a trigger that was
   * never wired. `ok: false` on a 201 means "saved, but a trigger did not
   * come up". Null on every read.
   */
  reconcile?: ReconcileResponse | null;
  /**
   * Plain-English rendering of `workflow`, computed by the backend from the
   * graph (services/dsl_summary.py). Lets the Definitions list say what a row
   * DOES without the reader opening the JSON.
   *
   * Null when the DSL could not be summarised (a legacy row of an older shape),
   * and absent entirely when talking to a data-plane that predates the feature —
   * so every consumer must null-guard rather than assume it is there.
   */
  summary?: DefinitionSummary | null;
  is_active_version: boolean;
  /** Kill-switch on the active version. False = every subscription
   * pointing at this WorkflowKey is paused (dispatcher acks events
   * without firing). True = subscriptions resume on next event. */
  is_enabled: boolean;
}

// ---------------------------------------------------------------------------
// Server-driven grids — one list contract shared by every grid endpoint
// (``{rows, total, limit, offset}``; ``sort_by`` is a per-endpoint allow-list
// the backend 422s outside of; ``limit`` is clamped to 1..200 server-side).
// ---------------------------------------------------------------------------

/** Server-sortable columns on ``GET /definitions/grid`` — mirrors the backend
 *  ``WorkflowDefinitionDAO.SORT_COLUMNS`` allow-list. */
export type DefinitionSortField =
  | "name"
  | "workflow_key"
  | "version"
  | "node_count"
  | "updated_at"
  | "is_enabled";

/** One Definitions-page row: the SHOWN version of a ``WorkflowKey`` (the
 *  active version, else the highest — resolved in SQL, one row per key) plus
 *  the two grid-only fields. Still a full ``DefinitionResponse`` so the View
 *  dialog, Run-now and the blurb work on it unchanged. */
export interface DefinitionGridRow extends DefinitionResponse {
  /** How many versions this key has (the shown one included). */
  version_count: number;
  /** tz-aware UTC ISO string of the shown version's ``UpdatedAtUtc``. */
  updated_at_utc: string;
}

export interface DefinitionGridResponse {
  rows: DefinitionGridRow[];
  total: number;
  limit: number;
  offset: number;
}

/** A filter-dropdown entry from ``GET /definitions/keys`` — no DSL, no
 *  version; one per ``WorkflowKey``, ordered by name. */
export interface DefinitionKeyResponse {
  workflow_key: string;
  name: string;
  is_enabled: boolean;
}

/** One subscription whose stored routing disagrees with its own DSL. */
export interface SubscriptionDrift {
  subscription_id: string;
  workflow_key: string;
  trigger_id: string;
  is_active: boolean;
  stored_stream: string;
  expected_stream: string;
  stored_filter: Record<string, unknown>;
  expected_filter: Record<string, unknown>;
  /** Human-readable, and the only place that says when a repair will NOT help. */
  reason: string;
}

export interface ReconcileTriggerOutcome {
  trigger_id: string;
  action: string; // created | updated | skipped | failed
  subscription_id: string | null;
  error: string | null;
}

export interface ReconcileResponse {
  workflow_key: string;
  version: number;
  ok: boolean;
  triggers: ReconcileTriggerOutcome[];
  orphaned: string[];
}

export interface CreateDefinitionRequest {
  workflow_key: string;
  name: string;
  workflow: Record<string, unknown>;
}

export interface NewVersionRequest {
  name: string;
  workflow: Record<string, unknown>;
}

export interface SetDefinitionEnabledRequest {
  enabled: boolean;
}

/** Operator config for the active version, split three ways so the Config
 * tab can render one field per declared key with its current value.
 * - ``defaults``: the keys/values authored in the DSL's top-level ``config``
 *   map (the tunable knobs + their defaults).
 * - ``overlay``: the operator's saved overrides (empty = none).
 * - ``effective``: ``defaults`` merged with ``overlay`` (overlay wins) — what
 *   ``${config.*}`` resolves to at fire time. */
export interface WorkflowConfigResponse {
  workflow_key: string;
  version: number;
  defaults: Record<string, unknown>;
  overlay: Record<string, unknown>;
  effective: Record<string, unknown>;
  /** Per key, the template's description of it when this workflow is a
   * template instance (type, label, description, enum/options, min/max).
   * Empty for a hand-authored workflow. */
  schema?: Record<string, ConfigFieldSchema>;
}

export interface SetWorkflowConfigRequest {
  /** FULLY REPLACES the prior overlay — omit a key to revert it to its DSL
   * default; send ``{}`` to clear all overrides. Every key must be declared
   * in the active version's DSL ``config`` (unknown keys 422). */
  overlay: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// DLQ — aggregated view of failed events across producers
// ---------------------------------------------------------------------------

export interface DlqRow {
  producer: string;
  id: number;
  event_id: string;
  stream: string;
  payload: string;
  attempts: number;
  last_error: string | null;
  enqueued_at_utc: string;
  last_attempt_at_utc: string | null;
}

/** Sort keys the DLQ list accepts — mirrors lara-data-plane
 *  ``DLQ_SORT_COLUMNS`` (and business2cloud ``FailedOutboxSort.Columns``)
 *  key for key; every producer applies the sort in its own SQL. */
export type DlqSortField =
  | "enqueued_at"
  | "event_id"
  | "stream"
  | "attempts"
  | "last_error";

export interface DlqProducerSlice {
  producer: string;
  /** The producer's own count under the SAME search predicate as `rows`
   *  (not just this page). */
  total: number;
  rows: DlqRow[];
  /** Populated when the producer was unreachable. UI renders a banner;
   *  the rest of the response is still usable. */
  error: string | null;
}

export interface DlqResponse {
  /** Sum of every reachable producer's `total`. Unreachable producers
   *  don't contribute (operator-friendly: count is what we can see). */
  total: number;
  by_producer: DlqProducerSlice[];
}

export interface RunScheduleNowResponse {
  workflow_key: string;
  /** Number of schedule triggers actually fired (skips triggers with no
   *  backing subscription). */
  triggers_fired: number;
  /** Total executions started across all fired triggers (one per fanOut row,
   *  or one for a single-fire schedule). */
  executions_fired: number;
  execution_ids: string[];
}

// ---------------------------------------------------------------------------
// Projects — ordered groupings of workflows (one outcome / pipeline)
// ---------------------------------------------------------------------------

/**
 * One member of a project — a workflow OR a connector.
 *
 * A project is the source of a *working* workflow, and a workflow does not
 * work without its connections, so a member row points at one or the other.
 * `kind` is the discriminator; exactly one of `workflow_key` /
 * `client_connector_id` is set.
 */
export interface ProjectMember {
  kind: "workflow" | "connector";
  /** Null on a connector member. */
  workflow_key: string | null;
  /** Null on a workflow member. `b2c.B2CClientConnector.Id`. */
  client_connector_id: number | null;
  order_index: number;
}

/**
 * The WORKFLOW members of a project, by key.
 *
 * A project also holds connector members (`workflow_key === null`). Anything
 * that judges workflows — the Definitions "in project" map, the project health
 * roll-up, the Spaces status widget — must go through this, because a
 * connector counted as a workflow looks like a workflow that has NEVER RUN and
 * drags the whole project to "degraded" purely for having a connection
 * attached. Nothing about that failure is loud; the widget just turns amber.
 */
export function workflowMemberKeys(members: ProjectMember[]): string[] {
  return members
    .filter((m) => m.workflow_key !== null)
    .map((m) => m.workflow_key as string);
}

/** One member in a requested order, for `reorderProjectMembers`. */
export interface MemberRef {
  kind: "workflow" | "connector";
  workflow_key?: string | null;
  client_connector_id?: number | null;
}

export interface WorkflowProject {
  id: string;
  organisation_id: string;
  project_key: string;
  name: string;
  description: string | null;
  /** WorkflowKey of the pipeline's entry point, or null if not nominated. */
  entry_workflow_key: string | null;
  member_count: number;
  /** Ordered members — populated on the detail (get-one) response; empty on
   *  the list response. */
  members: ProjectMember[];
}

/** Server-sortable columns on ``GET /projects/grid`` — mirrors the backend
 *  ``WorkflowProjectDAO.SORT_COLUMNS`` allow-list. */
export type ProjectSortField =
  | "name"
  | "project_key"
  | "entry_workflow_key"
  | "member_count"
  | "updated_at";

/** ``GET /projects/grid`` — rows are list-shaped ``WorkflowProject``s
 *  (``members: []``); ``member_count`` is computed server-side. */
export interface ProjectGridResponse {
  rows: WorkflowProject[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateProjectRequest {
  project_key: string;
  name: string;
  description?: string | null;
  entry_workflow_key?: string | null;
}

export interface UpdateProjectRequest {
  name?: string;
  description?: string | null;
  entry_workflow_key?: string | null;
  /** entry_workflow_key omitted = unchanged; set this true to null it out. */
  clear_entry?: boolean;
}

export interface RunProjectMemberResult {
  workflow_key: string;
  /** An active version exists for this member. */
  found: boolean;
  /** The member has at least one trigger.schedule (else it's reported + skipped). */
  has_schedule: boolean;
  executions_fired: number;
  execution_ids: string[];
}

export interface RunProjectResponse {
  project_id: string;
  /** Members that were schedule-capable and dispatched. */
  members_fired: number;
  executions_fired: number;
  results: RunProjectMemberResult[];
}

export interface SkippedMember {
  workflow_key: string;
  /** The project it already belongs to (one-project-per-workflow). */
  existing_project_id: string;
}

export interface BulkAddResponse {
  project: WorkflowProject;
  skipped: SkippedMember[];
}

/** One org-wide workflow→project membership — drives the Definitions list's
 * "Project" column. */
export interface MembershipEntry {
  workflow_key: string;
  project_id: string;
  project_name: string;
}

// ---------------------------------------------------------------------------
// Workflow chains — a workflow starts after other workflows finish their run
// for the same event (dp `trigger.workflow_completed`, plan
// workflow-chaining-after-workflow). Mirrors dp `api/v2/projects.py`
// ChainBuild*/ChainRuns* and `api/v2/definitions.py` AfterWorkflowTriggerRequest
// / DslEditResult.
// ---------------------------------------------------------------------------

export const AFTER_WORKFLOW_TRIGGER_KIND = "trigger.workflow_completed";

/** One member of a chain build. entry: the first member (after-workflow
 *  triggers removed) · converted: its source-sync start became the
 *  after-workflow trigger · rewired: an after-workflow trigger re-pointed or
 *  added · unchanged: nothing published. */
export interface ChainBuildMember {
  workflow_key: string;
  action: "entry" | "converted" | "rewired" | "unchanged";
  version: number;
  /** The publish-time reconcile's ok; null when nothing was published. */
  reconcile_ok: boolean | null;
  is_enabled: boolean;
}

export interface ChainBuildResponse {
  project: WorkflowProject;
  members: ChainBuildMember[];
}

export interface ChainLink {
  upstream: string;
  downstream: string;
}

export type ChainStepStatus =
  | "started"
  | "completed"
  | "failed"
  | "paused"
  | "pending";

export interface ChainStep {
  workflow_key: string;
  /** pending: no ledger row for this run yet (not reached, or never due). */
  status: ChainStepStatus;
  executions_fired: number;
  started_at_utc: string | null;
  completed_at_utc: string | null;
  chain_event_id: string | null;
}

export interface ChainRun {
  origin_event_id: string;
  started_at_utc: string;
  /** One per workflow member, in member order. */
  steps: ChainStep[];
}

export interface ChainRunsResponse {
  links: ChainLink[];
  /** One page of runs, newest first. */
  runs: ChainRun[];
  /** Every run of the chain (the pager's total), and the page asked for. */
  total: number;
  limit: number;
  offset: number;
}

export interface AfterWorkflowTriggerRequest {
  /** The workflow(s) this one starts after — all of them, for one event. */
  after_workflow_keys: string[];
  /** An existing after-workflow trigger to edit, or a new one's id (backend
   *  default ``on_chain``). Ignored with convert_start. */
  trigger_id?: string | null;
  /** Turn the source-sync start into the after-workflow trigger in place
   *  (same id, entry node and fan-out — its on/off switch is kept). */
  convert_start?: boolean;
}

/** ``changed`` false: the edit produced the active version's DSL and nothing
 *  was published. */
export interface DslEditResult {
  changed: boolean;
  definition: DefinitionResponse;
}

// ---------------------------------------------------------------------------
// API surface
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Workflow templates (dp `api/v2/templates.py`) — PLATFORM-ADMIN ONLY.
// ---------------------------------------------------------------------------

/** The input kinds dp's templates declare (`registry.TemplateInput.kind`). */
export type WorkflowTemplateInputKind =
  | "connector_key"
  | "sql_name"
  | "date"
  | "text"
  | "integer";

/** One value the operator supplies when switching a template on. `kind` picks the
 *  control; an unknown kind (a newer dp) renders as a plain text box. */
export interface WorkflowTemplateInput {
  name: string;
  label: string;
  description: string;
  kind: WorkflowTemplateInputKind | string;
  /** The connector input this one's pick-list is read from: a `sql_name` input lists
   *  the views in the org's control schema on that connector's target database. No
   *  template takes a schema input — dp derives every schema (`derived_schemas`). */
  depends_on?: string | null;
}

/** How the form draws one config key. `fee_mappings` is the repeatable
 *  mapping editor, `xero_tax_rate` a search-picker over the mirror's ACTIVE tax rates;
 *  the rest are plain fields. */
export interface WorkflowTemplateConfigField {
  type: "date" | "integer" | "string" | "enum" | "multiselect" | "fee_mappings" | "xero_tax_rate" | "workflow_key" | string;
  label: string;
  description?: string;
  minimum?: number;
  maximum?: number;
  enum?: string[];
  /** `multiselect` only: the values that can be ticked, in the template's order. dp's
   *  default and stored value is a comma-separated list of them (`"Name,TaxNumber"`). */
  options?: string[];
  /** The connector input this field's pick-lists read from (`fee_mappings` and
   *  `xero_tax_rate` → `mirror_connector_key`); dp resolves that connector's own
   *  `target.schema` server-side. */
  depends_on?: string | null;
}

/** One ACTIVE contact in a Xero mirror schema (`B2CXContact`). */
export interface XeroMirrorContact {
  id: string;
  name: string;
}

/** One ACTIVE GL account in a Xero mirror schema (`B2CXAccount`). */
export interface XeroMirrorAccount {
  id: string;
  code: string;
  name: string;
  type: string;
}

/** One ACTIVE tax rate in a Xero mirror (`B2CXTaxRate`). `tax_type` is what the
 *  template stores; `rate` is Xero's DisplayTaxRate as text, "" when NULL. */
export interface XeroMirrorTaxRate {
  tax_type: string;
  name: string;
  rate: string;
}

/** A mirror pick-list search: at most 50 items; `truncated` when there are more. */
export interface XeroMirrorList<T> {
  items: T[];
  truncated: boolean;
}

/** A template's "How it works": hand-written in the template's manifest, except `steps`,
 *  which dp derives from the template's own workflow (the same summariser the definitions
 *  page's "What this does" uses). */
export interface WorkflowTemplateHowItWorks {
  summary: string;
  trigger: string;
  rules: { title: string; rule: string; recon_code?: string | null }[];
  writes: string;
  recon: { code: string; meaning: string }[];
  prerequisites: string[];
  steps: StepSummary[];
}

export interface WorkflowTemplate {
  key: string;
  /** MyApp / status tables the template creates per ORG (dp registry `describe()`). A
   *  template declaring either can only be switched on into the default integration. */
  myapp_tables?: { name: string }[];
  status_tables?: { name: string }[];
  name: string;
  description: string;
  /** The system the template writes to ("Xero"; later "Cin7", …) — the gallery groups by
   *  it. Optional: a dp from before the field shipped omits it (the gallery files those
   *  under "Other"). */
  provider?: string | null;
  /** The static "How it works" breakdown (dp plan xero-templates-contacts-supplier-categories
   *  §3b). Optional: a dp from before it sends none, and the panel is then not drawn. */
  how_it_works?: WorkflowTemplateHowItWorks | null;
  workflow_key: string;
  inputs: WorkflowTemplateInput[];
  config_defaults: Record<string, unknown>;
  config_schema: Record<string, WorkflowTemplateConfigField>;
}

/** One row of the managed-tenant registry (dp `[dp_workflows].[ManagedTenant]`). Every
 *  template switched on for the org lands in `control_schema`, in `canonical_folder`. */
export interface ManagedTenant {
  organisation_id: string;
  tenant: string;
  short_name: string;
  control_schema: string;
  canonical_folder: string;
}

export interface RegisterManagedTenantRequest {
  org_id: string;
  /** 2–8 lower-case letters/digits; the control schema becomes `<short_name>-control`. */
  short_name: string;
  /** The canonical folder slug under `schemas/tenants/`. */
  tenant: string;
}

/** One source→target integration of a managed tenant (dp `[dp_workflows].[ManagedTenantIntegration]`,
 *  plan per-integration-control-schemas D1): its own control schema and canonical folder.
 *  Every org has exactly one `is_default` integration, mirrored on its `ManagedTenant` row. */
export interface ManagedTenantIntegration {
  organisation_id: string;
  /** `<source-short>-<target>`, e.g. `ss-xero`; the backfilled default is `control`. */
  integration_key: string;
  display_name: string;
  control_schema: string;
  canonical_folder: string;
  drift_connector_key: string | null;
  is_default: boolean;
}

export interface RegisterIntegrationRequest {
  /** Lower-case slug; dp derives the control schema and canonical folder from it (D4). */
  integration_key: string;
  display_name?: string;
  drift_connector_key?: string;
}

export interface InstantiateWorkflowTemplateRequest {
  org_id: string;
  /** Strings, except an `integer`-kind input, which is sent as a number. */
  inputs: Record<string, string | number>;
  config: Record<string, unknown>;
  /** Which of the org's integrations the instance renders into. Omitted: the org's
   *  default integration — exactly the request an org with one integration always sent. */
  integration_key?: string;
}

/** dp `GET …/templates/{key}/instances/current` (`CurrentInstanceResponse`): what the org's
 *  existing instance was switched on with, read back from its stored workflow and canonical
 *  views (dp `templates/instance_inputs.py`). The re-switch-on form opens on it. */
export interface CurrentTemplateInstance {
  template_key: string;
  workflow_key: string;
  /** The latest version — the one read. */
  version: number;
  is_enabled: boolean;
  /** Each input read back, by name. An input not read back is absent and in `unrecovered`. */
  inputs: Record<string, string | number>;
  /** Config by key as the workflow holds it: a multiselect is its comma-separated string,
   *  fee mappings a list. A key absent here runs on the template default. */
  config: Record<string, unknown>;
  /** Input names and config keys dp could not read back (the template gained them since, or
   *  changed the text that carried them): left for the operator, never guessed. */
  unrecovered: string[];
}

export interface InstantiateWorkflowTemplateResponse {
  template_key: string;
  workflow_key: string;
  tenant: string;
  short_name: string;
  canonical_folder: string;
  control_schema: string;
  /** True when this switch-on created the control schema on the customer's database. */
  control_schema_created: boolean;
  definition: {
    id: string;
    version: number;
    is_enabled: boolean;
    created: boolean;
    /** "reused": an identical instance already existed (a re-run); "replaced": a
     *  still-disabled instance was replaced with these inputs (a new version). */
    outcome: "created" | "reused" | "replaced";
    canonical_version_id: string | null;
  };
  version_ids: Record<string, string | null>;
  /** Schemas a replace moved the instance away from — their live objects and canonical
   *  folder are left in place for an operator to remove. Empty unless outcome is "replaced". */
  superseded_schemas: string[];
  tables_created: string[];
  tables_added: string[];
  views_applied: string[];
  blocked: string[];
  post_sweep_ok: boolean;
}

export const workflowsApi = {
  /**
   * Run a workflow definition end-to-end via the walker. Returns either a
   * completed result, a failed result with an error message, or a
   * suspended result that can be resumed by ``execution_id``.
   */
  async executeWorkflow(
    body: ExecuteWorkflowRequest,
    token: string | undefined,
  ): Promise<ExecutionResponse> {
    return portalFetch<ExecutionResponse>(
      `${API_BASE_PATH}/workflows/execute`,
      { method: "POST", token, body },
    );
  },

  /**
   * WS2 "describe it → workflow": turn a natural-language description into a
   * validated workflow DSL. The service builds a contract from live sources,
   * asks the agentic generator, then runs the authoritative
   * Workflow.model_validate gate (re-prompting on errors up to max_attempts).
   * `ok` → drop `dsl` on the canvas and Test-run before publishing.
   */
  async generateDsl(
    prompt: string,
    token: string | undefined,
    maxAttempts?: number,
  ): Promise<GenerateDslResponse> {
    return portalFetch<GenerateDslResponse>(
      `${API_BASE_PATH}/workflows/dsl/generate`,
      {
        method: "POST",
        token,
        body:
          maxAttempts != null ? { prompt, max_attempts: maxAttempts } : { prompt },
      },
    );
  },

  /**
   * Continue a previously-suspended execution with a human decision. The
   * service loads the DSL, trigger snapshot and node outputs from MSSQL by
   * id — the caller only sends the decision plus optional metadata.
   */
  async resumeExecution(
    executionId: string,
    body: ResumeExecutionRequest,
    token: string | undefined,
  ): Promise<ExecutionResponse> {
    return portalFetch<ExecutionResponse>(
      `${API_BASE_PATH}/workflows/executions/${executionId}/resume`,
      { method: "POST", token, body },
    );
  },

  /**
   * Fetch the latest persisted state for an execution. Used to render a
   * result without holding the snapshot client-side.
   */
  async getExecution(
    executionId: string,
    token: string | undefined,
  ): Promise<ExecutionResponse> {
    return portalFetch<ExecutionResponse>(
      `${API_BASE_PATH}/workflows/executions/${executionId}`,
      { method: "GET", token },
    );
  },

  /**
   * List one page of recent executions for the current org. Optional
   * ``status`` filter narrows to one bucket. Rows only — the per-status counts
   * (chips, pager total) are ``countExecutions``.
   */
  async listExecutions(
    token: string | undefined,
    options?: {
      status?: ExecutionListStatus;
      workflowKey?: string;
      projectId?: string;
      /** Substring match on workflow key, project name, trigger event id and
       *  error message. Pass the same term to ``countExecutions`` so the
       *  chips describe the listed set. */
      search?: string;
      sortBy?: ExecutionSortField;
      sortOrder?: "asc" | "desc";
      limit?: number;
      offset?: number;
    },
  ): Promise<ExecutionListResponse> {
    const params = new URLSearchParams();
    if (options?.search) params.set("search", options.search);
    if (options?.status) params.set("status", options.status);
    if (options?.workflowKey) params.set("workflow_key", options.workflowKey);
    if (options?.projectId) params.set("project_id", options.projectId);
    if (options?.sortBy) params.set("sort_by", options.sortBy);
    if (options?.sortOrder) params.set("sort_order", options.sortOrder);
    if (options?.limit !== undefined) params.set("limit", String(options.limit));
    if (options?.offset !== undefined) params.set("offset", String(options.offset));
    const qs = params.toString();
    return portalFetch<ExecutionListResponse>(
      `${API_BASE_PATH}/workflows/executions${qs ? `?${qs}` : ""}`,
      { method: "GET", token },
    );
  },

  /** Per-status execution counts, narrowed by the same workflow / project /
   *  search filters as ``listExecutions``. ``statuses`` counts only those
   *  (repeated ``status=`` params) — the overview's running / suspended pair
   *  is two index seeks, not a pass over the org's history. */
  async countExecutions(
    token: string | undefined,
    options?: {
      workflowKey?: string;
      projectId?: string;
      search?: string;
      statuses?: ExecutionListStatus[];
    },
  ): Promise<ExecutionCountsResponse> {
    const params = new URLSearchParams();
    if (options?.search) params.set("search", options.search);
    if (options?.workflowKey) params.set("workflow_key", options.workflowKey);
    if (options?.projectId) params.set("project_id", options.projectId);
    for (const s of options?.statuses ?? []) params.append("status", s);
    const qs = params.toString();
    return portalFetch<ExecutionCountsResponse>(
      `${API_BASE_PATH}/workflows/executions/counts${qs ? `?${qs}` : ""}`,
      { method: "GET", token },
    );
  },

  /** Last run + schedule-aware overdue per workflow (one row per workflow_key).
   *  Powers the "Last run" column + the project health rollup. */
  async listLastRuns(token: string | undefined): Promise<LastRunsResponse> {
    return portalFetch<LastRunsResponse>(
      `${API_BASE_PATH}/workflows/last-runs`,
      { method: "GET", token },
    );
  },

  // -------------------------------------------------------------------------
  // Stream catalog — populates the subscription designer's stream picker.
  // -------------------------------------------------------------------------

  async listStreams(
    token: string | undefined,
    source?: string,
  ): Promise<StreamCatalogResponse> {
    const query = source
      ? `?source=${encodeURIComponent(source)}`
      : "";
    return portalFetch<StreamCatalogResponse>(
      `${API_BASE_PATH}/workflows/streams${query}`,
      { method: "GET", token },
    );
  },

  async getEnvelopeSchema(
    token: string | undefined,
  ): Promise<Record<string, unknown>> {
    return portalFetch<Record<string, unknown>>(
      `${API_BASE_PATH}/workflows/streams/envelope-schema`,
      { method: "GET", token },
    );
  },

  // -------------------------------------------------------------------------
  // Picker registry — uniform per-org dropdown source for the canvas
  // Inspector. The backend dispatches on ``ref_type`` (redis.stream,
  // myapp.form, messaging.conversation, b2c.connector, …) and returns
  // the same response shape regardless. Adding a new picker source is
  // a one-resolver change server-side; this client doesn't need to know.
  // -------------------------------------------------------------------------

  async listPicker(
    refType: string,
    token: string | undefined,
  ): Promise<PickerResponse> {
    return portalFetch<PickerResponse>(
      `${API_BASE_PATH}/workflows/pickers/${encodeURIComponent(refType)}`,
      { method: "GET", token },
    );
  },

  async getStreamDepth(
    token: string | undefined,
  ): Promise<StreamDepthResponse> {
    return portalFetch<StreamDepthResponse>(
      `${API_BASE_PATH}/workflows/streams/depth`,
      { method: "GET", token },
    );
  },

  /** Compile-check a single CEL field value (WS3a <CelField> inline validation). */
  async validateExpression(
    expression: string,
    token: string | undefined,
  ): Promise<{ valid: boolean; error?: string | null }> {
    return portalFetch<{ valid: boolean; error?: string | null }>(
      `${API_BASE_PATH}/workflows/dsl/expr/validate`,
      { method: "POST", token, body: { expression } },
    );
  },

  /** List a MyApp table's field definitions (WS3c data-mapper). */
  async listFormFields(
    formId: string,
    token: string | undefined,
  ): Promise<MyAppFieldDef[]> {
    return portalFetch<MyAppFieldDef[]>(
      `${API_BASE_PATH}/workflows/forms/${encodeURIComponent(formId)}/fields`,
      { method: "GET", token },
    );
  },

  /**
   * List an org's integration secrets — NAMES only, never values (WS5). The
   * org is taken from the gateway auth header, not a parameter. Server-side
   * paginated / searched / ordered → {items, total}.
   */
  async listSecrets(
    opts: ListSecretsOptions,
    token: string | undefined,
  ): Promise<SecretListResponse> {
    const qs = new URLSearchParams();
    if (opts.page) qs.set("page", String(opts.page));
    if (opts.page_size) qs.set("page_size", String(opts.page_size));
    if (opts.search) qs.set("search", opts.search);
    if (opts.sort) qs.set("sort", opts.sort);
    if (opts.order) qs.set("order", opts.order);
    const query = qs.toString();
    return portalFetch<SecretListResponse>(
      `${API_BASE_PATH}/workflows/secrets${query ? `?${query}` : ""}`,
      { method: "GET", token },
    );
  },

  /** Create or rotate an org secret (WS5). Setting an existing name replaces
   * its value. The value is stored encrypted and never returned. */
  async upsertSecret(
    body: { name: string; value: string; description?: string | null },
    token: string | undefined,
  ): Promise<void> {
    await portalFetch<void>(`${API_BASE_PATH}/workflows/secrets`, {
      method: "POST",
      token,
      body,
    });
  },

  /** Delete an org secret by name (WS5). */
  async deleteSecret(
    name: string,
    token: string | undefined,
  ): Promise<void> {
    await portalFetch<void>(
      `${API_BASE_PATH}/workflows/secrets/${encodeURIComponent(name)}`,
      { method: "DELETE", token },
    );
  },

  /** Introspect a B2C connector's schema (WS3c SQL query builder). */
  async describeConnectorSchema(
    connectorKey: string,
    opts: { direction?: string; schema?: string },
    token: string | undefined,
  ): Promise<ConnectorSchema> {
    const qs = new URLSearchParams();
    if (opts.direction) qs.set("direction", opts.direction);
    if (opts.schema) qs.set("schema", opts.schema);
    const query = qs.toString();
    return portalFetch<ConnectorSchema>(
      `${API_BASE_PATH}/workflows/connectors/${encodeURIComponent(connectorKey)}/schema${query ? `?${query}` : ""}`,
      { method: "GET", token },
    );
  },

  /**
   * Run an author's SELECT read-only against the connector and return the
   * first ``limit`` rows + columns (WS3b — the canvas "Test query" button).
   * Reuses the exact ``B2CDataReader.fetch_select`` path the
   * ``logic.sql_query`` node uses, so a preview can never mutate data. A bad
   * SELECT / unresolvable connector surfaces as a 400 from the backend.
   */
  async previewQuery(
    connectorKey: string,
    body: QueryPreviewRequest,
    token: string | undefined,
  ): Promise<QueryPreviewResult> {
    return portalFetch<QueryPreviewResult>(
      `${API_BASE_PATH}/workflows/connectors/${encodeURIComponent(connectorKey)}/query/preview`,
      { method: "POST", token, body },
    );
  },

  // -------------------------------------------------------------------------
  // Subscriptions — read-only / diagnostic surface.
  //
  // Subscriptions are now system-managed: declaring a trigger in the
  // workflow DSL creates one automatically on publish (see backend's
  // ``subscription_reconciler``). Per-trigger pause/resume is the
  // ``setTriggerActive`` method below. The user-facing CRUD endpoints
  // (POST/PATCH/DELETE /workflows/subscriptions) have been removed from
  // the backend; calling them from the client would return 405.
  // -------------------------------------------------------------------------

  async listSubscriptions(
    token: string | undefined,
  ): Promise<SubscriptionResponse[]> {
    return portalFetch<SubscriptionResponse[]>(
      `${API_BASE_PATH}/workflows/subscriptions`,
      { method: "GET", token },
    );
  },

  /**
   * Pause / resume a single trigger on a workflow definition. Replaces
   * the legacy ``updateSubscription({id, is_active})`` flow with a knob
   * keyed on the trigger's identity instead of the underlying
   * subscription row's id — friendlier shape for the Triggers panel
   * on the workflow detail page.
   */
  async setTriggerActive(
    workflowKey: string,
    triggerId: string,
    body: SetTriggerActiveRequest,
    token: string | undefined,
  ): Promise<TriggerActiveResponse> {
    return portalFetch<TriggerActiveResponse>(
      `${API_BASE_PATH}/workflows/definitions/key/${encodeURIComponent(
        workflowKey,
      )}/triggers/${encodeURIComponent(triggerId)}/active`,
      { method: "PATCH", token, body },
    );
  },

  // -------------------------------------------------------------------------
  // Workflow definitions — authored DSL store. List, create, version.
  // Subscriptions point at frozen versions; editing a definition produces a
  // new version row, leaving running subscriptions untouched.
  // -------------------------------------------------------------------------

  // -------------------------------------------------------------------------
  // Subscription drift — a workflow's triggers can end up listening to
  // something its own DSL no longer says. Until DP #1302 a republish moved the
  // definition pointer and left the stored routing frozen at first publish, so
  // an edited connector_key never took effect. Haesloop's enrich trigger sat
  // like that for six weeks and fired zero times, looking perfectly healthy.
  // -------------------------------------------------------------------------

  async listSubscriptionDrift(
    token: string | undefined,
  ): Promise<SubscriptionDrift[]> {
    return portalFetch<SubscriptionDrift[]>(
      `${API_BASE_PATH}/workflows/subscriptions/drift`,
      { method: "GET", token },
    );
  },

  /**
   * Repair one workflow's subscriptions from its ACTIVE definition.
   *
   * Not a republish: no new version is created. Reconciliation used to run
   * only on publish, which made the remedy for a routing problem a version
   * bump — a version recording that nothing changed.
   */
  async reconcileSubscriptions(
    workflowKey: string,
    token: string | undefined,
  ): Promise<ReconcileResponse> {
    return portalFetch<ReconcileResponse>(
      `${API_BASE_PATH}/workflows/definitions/key/${encodeURIComponent(
        workflowKey,
      )}/reconcile`,
      { method: "POST", token },
    );
  },

  async listDefinitions(
    token: string | undefined,
  ): Promise<DefinitionResponse[]> {
    return portalFetch<DefinitionResponse[]>(
      `${API_BASE_PATH}/workflows/definitions`,
      { method: "GET", token },
    );
  },

  /** Definitions page grid: one row per WorkflowKey, paged/searched/sorted
   *  server-side. Every axis is a query param — never slice, filter or sort
   *  the returned page client-side. Omitted options are omitted from the
   *  query (the backend's defaults apply), never sent as ``undefined``. */
  async listDefinitionsGrid(
    token: string | undefined,
    options?: {
      search?: string;
      isEnabled?: boolean;
      projectId?: string;
      sortBy?: DefinitionSortField;
      sortOrder?: "asc" | "desc";
      limit?: number;
      offset?: number;
    },
  ): Promise<DefinitionGridResponse> {
    const params = new URLSearchParams();
    if (options?.search) params.set("search", options.search);
    if (options?.isEnabled !== undefined) {
      params.set("is_enabled", String(options.isEnabled));
    }
    if (options?.projectId) params.set("project_id", options.projectId);
    if (options?.sortBy) params.set("sort_by", options.sortBy);
    if (options?.sortOrder) params.set("sort_order", options.sortOrder);
    if (options?.limit !== undefined) params.set("limit", String(options.limit));
    if (options?.offset !== undefined) params.set("offset", String(options.offset));
    const qs = params.toString();
    return portalFetch<DefinitionGridResponse>(
      `${API_BASE_PATH}/workflows/definitions/grid${qs ? `?${qs}` : ""}`,
      { method: "GET", token },
    );
  },

  /** (key, name, enabled) per WorkflowKey — the feed for filter dropdowns.
   *  No DSL, so it is cheap to load once per page mount. */
  async listDefinitionKeys(
    token: string | undefined,
  ): Promise<DefinitionKeyResponse[]> {
    return portalFetch<DefinitionKeyResponse[]>(
      `${API_BASE_PATH}/workflows/definitions/keys`,
      { method: "GET", token },
    );
  },

  async createDefinition(
    body: CreateDefinitionRequest,
    token: string | undefined,
  ): Promise<DefinitionResponse> {
    return portalFetch<DefinitionResponse>(
      `${API_BASE_PATH}/workflows/definitions`,
      { method: "POST", token, body },
    );
  },

  async newDefinitionVersion(
    workflowKey: string,
    body: NewVersionRequest,
    token: string | undefined,
  ): Promise<DefinitionResponse> {
    return portalFetch<DefinitionResponse>(
      `${API_BASE_PATH}/workflows/definitions/${encodeURIComponent(workflowKey)}/versions`,
      { method: "POST", token, body },
    );
  },

  async getDefinition(
    id: string,
    token: string | undefined,
  ): Promise<DefinitionResponse> {
    return portalFetch<DefinitionResponse>(
      `${API_BASE_PATH}/workflows/definitions/${id}`,
      { method: "GET", token },
    );
  },

  async getLatestDefinitionForKey(
    workflowKey: string,
    token: string | undefined,
  ): Promise<DefinitionResponse> {
    return portalFetch<DefinitionResponse>(
      `${API_BASE_PATH}/workflows/definitions/key/${encodeURIComponent(workflowKey)}/latest`,
      { method: "GET", token },
    );
  },

  /** Flip the kill switch on a WorkflowKey's active version. ``enabled=false``
   * pauses every subscription pointing at it (dispatcher acks events
   * without firing); ``enabled=true`` resumes. Returns the updated
   * definition row so callers can refresh their local state. */
  async setDefinitionEnabled(
    workflowKey: string,
    enabled: boolean,
    token: string | undefined,
  ): Promise<DefinitionResponse> {
    return portalFetch<DefinitionResponse>(
      `${API_BASE_PATH}/workflows/definitions/key/${encodeURIComponent(workflowKey)}/enabled`,
      {
        method: "PATCH",
        token,
        body: { enabled } satisfies SetDefinitionEnabledRequest,
      },
    );
  },

  /** Read the active version's operator config (defaults + overlay +
   * effective). Powers the workflow Config tab. */
  async getDefinitionConfig(
    workflowKey: string,
    token: string | undefined,
  ): Promise<WorkflowConfigResponse> {
    return portalFetch<WorkflowConfigResponse>(
      `${API_BASE_PATH}/workflows/definitions/key/${encodeURIComponent(workflowKey)}/config`,
      { method: "GET", token },
    );
  },

  /** Persist operator config overrides on the active version — no version
   * bump (same posture as the enable/pause switch). ``overlay`` fully
   * replaces the prior overlay; takes effect on the next fire. */
  async setDefinitionConfig(
    workflowKey: string,
    overlay: Record<string, unknown>,
    token: string | undefined,
  ): Promise<WorkflowConfigResponse> {
    return portalFetch<WorkflowConfigResponse>(
      `${API_BASE_PATH}/workflows/definitions/key/${encodeURIComponent(workflowKey)}/config`,
      {
        method: "PATCH",
        token,
        body: { overlay } satisfies SetWorkflowConfigRequest,
      },
    );
  },

  /** Retire a workflow entirely: hard-deletes every version of this
   * WorkflowKey + its subscriptions (execution history is preserved). Returns
   * `{ workflow_key, versions_deleted }`. */
  async deleteDefinition(
    workflowKey: string,
    token: string | undefined,
  ): Promise<{ workflow_key: string; versions_deleted: number }> {
    return portalFetch<{ workflow_key: string; versions_deleted: number }>(
      `${API_BASE_PATH}/workflows/definitions/key/${encodeURIComponent(workflowKey)}`,
      { method: "DELETE", token },
    );
  },

  /** Fire a workflow's trigger.schedule(s) on demand — identical to a cron
   * tick (fanOut expands, one execution per discovered row). Saves waiting
   * up to 24h for the next scheduled run. 422 if the workflow has no schedule
   * trigger; 404 if the key has no active version. Owner/admin only. */
  async runScheduleNow(
    workflowKey: string,
    token: string | undefined,
  ): Promise<RunScheduleNowResponse> {
    return portalFetch<RunScheduleNowResponse>(
      `${API_BASE_PATH}/workflows/${encodeURIComponent(workflowKey)}/run-schedule`,
      { method: "POST", token },
    );
  },

  /** Fire the PUBLISHED active version of a workflow by key with a given
   * trigger + payload, synchronously. Pass ``dry_run: true`` to preview it
   * with no side effects (WS6) — used by "Re-run as test" on the executions
   * page. Returns the full ExecutionResult (node_outputs etc.). */
  async runWorkflowByKey(
    workflowKey: string,
    body: {
      trigger_id: string;
      payload?: Record<string, unknown>;
      dry_run?: boolean;
      live_reads?: boolean;
      /** `false`: fire-and-forget; returns at once with `status: "running"`
       *  and the `execution_id` (dp executions.py `RunWorkflowByKeyRequest`). */
      wait?: boolean;
    },
    token: string | undefined,
  ): Promise<ExecutionResponse> {
    return portalFetch<ExecutionResponse>(
      `${API_BASE_PATH}/workflows/${encodeURIComponent(workflowKey)}/run`,
      { method: "POST", token, body },
    );
  },

  // -------------------------------------------------------------------------
  // Projects — group workflows into one named outcome/pipeline.
  // -------------------------------------------------------------------------

  async listProjects(
    token: string | undefined,
  ): Promise<WorkflowProject[]> {
    return portalFetch<WorkflowProject[]>(
      `${API_BASE_PATH}/workflows/projects`,
      { method: "GET", token },
    );
  },

  /** Projects page grid — paged, searched and sorted server-side. Same
   *  contract as ``listDefinitionsGrid``; omitted options are omitted. */
  async listProjectsGrid(
    token: string | undefined,
    options?: {
      search?: string;
      sortBy?: ProjectSortField;
      sortOrder?: "asc" | "desc";
      limit?: number;
      offset?: number;
    },
  ): Promise<ProjectGridResponse> {
    const params = new URLSearchParams();
    if (options?.search) params.set("search", options.search);
    if (options?.sortBy) params.set("sort_by", options.sortBy);
    if (options?.sortOrder) params.set("sort_order", options.sortOrder);
    if (options?.limit !== undefined) params.set("limit", String(options.limit));
    if (options?.offset !== undefined) params.set("offset", String(options.offset));
    const qs = params.toString();
    return portalFetch<ProjectGridResponse>(
      `${API_BASE_PATH}/workflows/projects/grid${qs ? `?${qs}` : ""}`,
      { method: "GET", token },
    );
  },

  async getProject(
    projectId: string,
    token: string | undefined,
  ): Promise<WorkflowProject> {
    return portalFetch<WorkflowProject>(
      `${API_BASE_PATH}/workflows/projects/${encodeURIComponent(projectId)}`,
      { method: "GET", token },
    );
  },

  async createProject(
    body: CreateProjectRequest,
    token: string | undefined,
  ): Promise<WorkflowProject> {
    return portalFetch<WorkflowProject>(
      `${API_BASE_PATH}/workflows/projects`,
      { method: "POST", token, body },
    );
  },

  async updateProject(
    projectId: string,
    body: UpdateProjectRequest,
    token: string | undefined,
  ): Promise<WorkflowProject> {
    return portalFetch<WorkflowProject>(
      `${API_BASE_PATH}/workflows/projects/${encodeURIComponent(projectId)}`,
      { method: "PATCH", token, body },
    );
  },

  async deleteProject(
    projectId: string,
    token: string | undefined,
  ): Promise<void> {
    await portalFetch<void>(
      `${API_BASE_PATH}/workflows/projects/${encodeURIComponent(projectId)}`,
      { method: "DELETE", token },
    );
  },

  /** Add a workflow to a project. 409 if it already belongs to another
   * project (one-project-per-workflow). Returns the project with its updated
   * member list. */
  async addProjectMember(
    projectId: string,
    workflowKey: string,
    token: string | undefined,
  ): Promise<WorkflowProject> {
    return portalFetch<WorkflowProject>(
      `${API_BASE_PATH}/workflows/projects/${encodeURIComponent(projectId)}/members`,
      { method: "POST", token, body: { workflow_key: workflowKey } },
    );
  },

  async removeProjectMember(
    projectId: string,
    workflowKey: string,
    token: string | undefined,
  ): Promise<void> {
    await portalFetch<void>(
      `${API_BASE_PATH}/workflows/projects/${encodeURIComponent(projectId)}/members/${encodeURIComponent(workflowKey)}`,
      { method: "DELETE", token },
    );
  },

  /** Set the declared run order. Pass the FULL member list — every member,
   * both kinds — in the desired order.
   *
   * `ordered_members`, not the older `ordered_workflow_keys`: OrderIndex is ONE
   * sequence per project across workflows AND connectors, so a request naming
   * only the workflows would renumber half the list from 0 and leave the
   * connectors on stale indexes, two members to a slot. The API refuses the
   * workflow-only form on a project that holds connectors, so sending it here
   * would simply 400. */
  async reorderProjectMembers(
    projectId: string,
    orderedMembers: MemberRef[],
    token: string | undefined,
  ): Promise<WorkflowProject> {
    return portalFetch<WorkflowProject>(
      `${API_BASE_PATH}/workflows/projects/${encodeURIComponent(projectId)}/members/order`,
      {
        method: "PATCH",
        token,
        body: { ordered_members: orderedMembers },
      },
    );
  },

  /** Add a connector to a project. The API verifies the org owns it (404 if
   * not — deliberately indistinguishable from a connector that doesn't
   * exist) and 503s rather than guessing if b2c is unreachable. */
  async addProjectConnectorMember(
    projectId: string,
    clientConnectorId: number,
    token: string | undefined,
  ): Promise<WorkflowProject> {
    return portalFetch<WorkflowProject>(
      `${API_BASE_PATH}/workflows/projects/${encodeURIComponent(projectId)}/connector-members`,
      {
        method: "POST",
        token,
        body: { client_connector_id: clientConnectorId },
      },
    );
  },

  /** Remove a connector from a project. The connector itself is untouched —
   * only the grouping link goes. */
  async removeProjectConnectorMember(
    projectId: string,
    clientConnectorId: number,
    token: string | undefined,
  ): Promise<void> {
    await portalFetch<void>(
      `${API_BASE_PATH}/workflows/projects/${encodeURIComponent(projectId)}/connector-members/${clientConnectorId}`,
      { method: "DELETE", token },
    );
  },

  /** Fire every member's schedule now, in declared order. Members without a
   * schedule trigger are reported (has_schedule=false) but skipped. */
  async runProject(
    projectId: string,
    token: string | undefined,
  ): Promise<RunProjectResponse> {
    return portalFetch<RunProjectResponse>(
      `${API_BASE_PATH}/workflows/projects/${encodeURIComponent(projectId)}/run`,
      { method: "POST", token },
    );
  },

  /** Add several workflows to a project at once. Keys already in another
   * project are skipped + returned in `skipped` (partial success), not an error. */
  async addProjectMembersBulk(
    projectId: string,
    workflowKeys: string[],
    token: string | undefined,
  ): Promise<BulkAddResponse> {
    return portalFetch<BulkAddResponse>(
      `${API_BASE_PATH}/workflows/projects/${encodeURIComponent(projectId)}/members/bulk`,
      { method: "POST", token, body: { workflow_keys: workflowKeys } },
    );
  },

  /** Run the project's workflows one after another, in this order — EVERY
   * workflow member, first = entry. Publishes a new version of each member
   * whose triggers change (enabled state inherited: never switches one on). */
  async buildProjectChain(
    projectId: string,
    orderedWorkflowKeys: string[],
    token: string | undefined,
  ): Promise<ChainBuildResponse> {
    return portalFetch<ChainBuildResponse>(
      `${API_BASE_PATH}/workflows/projects/${encodeURIComponent(projectId)}/chain`,
      {
        method: "PUT",
        token,
        body: { ordered_workflow_keys: orderedWorkflowKeys },
      },
    );
  },

  /** The project's chain links and one page of its chain runs, newest first
   * (``limit`` + ``offset``; ``total`` counts every run). Owner/admin only (the
   * gateway grants org_member nothing here). */
  async getProjectChainRuns(
    projectId: string,
    limit: number,
    token: string | undefined,
    offset = 0,
  ): Promise<ChainRunsResponse> {
    return portalFetch<ChainRunsResponse>(
      `${API_BASE_PATH}/workflows/projects/${encodeURIComponent(projectId)}/chain/runs?limit=${limit}&offset=${offset}`,
      { method: "GET", token },
    );
  },

  /** Add or edit a workflow's after-workflow trigger — publishes a new
   * version (a disabled workflow stays disabled). Owner/admin only. */
  async addAfterWorkflowTrigger(
    workflowKey: string,
    body: AfterWorkflowTriggerRequest,
    token: string | undefined,
  ): Promise<DslEditResult> {
    return portalFetch<DslEditResult>(
      `${API_BASE_PATH}/workflows/definitions/key/${encodeURIComponent(workflowKey)}/triggers/after-workflow`,
      { method: "POST", token, body },
    );
  },

  /** Org-wide workflow→project map for the Definitions list's Project column. */
  async listProjectMemberships(
    token: string | undefined,
  ): Promise<MembershipEntry[]> {
    return portalFetch<MembershipEntry[]>(
      `${API_BASE_PATH}/workflows/projects/memberships`,
      { method: "GET", token },
    );
  },

  // -------------------------------------------------------------------------
  // DLQ — aggregator view across producers; per-row requeue / discard
  // proxies to the relevant producer's admin endpoint.
  // -------------------------------------------------------------------------

  /**
   * Aggregated failed-event list. ``search`` / ``sortBy`` / ``sortOrder``
   * are forwarded by the DP to EVERY producer in lockstep (each sorts and
   * filters its own slice; there is no global order across producers), so a
   * slice's ``total`` reflects the search. Built like ``listExecutions``: an
   * omitted option is absent from the query string, never ``"undefined"``.
   */
  async listDlq(
    token: string | undefined,
    options?: {
      limit?: number;
      offset?: number;
      /** Substring match on event id, stream and last error (never payload);
       *  max 200 chars — the DP 422s and the producer 400s above that. */
      search?: string;
      sortBy?: DlqSortField;
      sortOrder?: "asc" | "desc";
    },
  ): Promise<DlqResponse> {
    const params = new URLSearchParams();
    if (options?.search) params.set("search", options.search);
    if (options?.sortBy) params.set("sort_by", options.sortBy);
    if (options?.sortOrder) params.set("sort_order", options.sortOrder);
    if (options?.limit !== undefined) params.set("limit", String(options.limit));
    if (options?.offset !== undefined) params.set("offset", String(options.offset));
    const qs = params.toString();
    return portalFetch<DlqResponse>(
      `${API_BASE_PATH}/workflows/dlq${qs ? `?${qs}` : ""}`,
      { method: "GET", token },
    );
  },

  async requeueDlqRow(
    producer: string,
    rowId: number,
    token: string | undefined,
  ): Promise<{ producer: string; id: number; requeued: boolean }> {
    return portalFetch(
      `${API_BASE_PATH}/workflows/dlq/${encodeURIComponent(producer)}/${rowId}/requeue`,
      { method: "POST", token },
    );
  },

  async discardDlqRow(
    producer: string,
    rowId: number,
    token: string | undefined,
  ): Promise<{ producer: string; id: number; discarded: boolean }> {
    return portalFetch(
      `${API_BASE_PATH}/workflows/dlq/${encodeURIComponent(producer)}/${rowId}/discard`,
      { method: "POST", token },
    );
  },

  /** Read-only, platform-admin: the workflow-template catalogue. */
  async listWorkflowTemplates(
    token: string | null | undefined,
  ): Promise<WorkflowTemplate[]> {
    const res = await portalFetch<{ templates: WorkflowTemplate[] }>(
      `${API_BASE_PATH}/workflows/templates`,
      { method: "GET", token: token ?? undefined },
    );
    return res.templates;
  },

  /** Read-only, platform-admin: every registered managed tenant. */
  async listManagedTenants(token: string | null | undefined): Promise<ManagedTenant[]> {
    const res = await portalFetch<{ managed_tenants: ManagedTenant[] }>(
      `${API_BASE_PATH}/workflows/managed-tenants`,
      { method: "GET", token: token ?? undefined },
    );
    return res.managed_tenants;
  },

  /** Read-only, platform-admin: one org's registration. Rejects with a 404
   *  `PortalApiError` when the org is not registered. */
  async getManagedTenant(orgId: string, token: string | null | undefined): Promise<ManagedTenant> {
    return portalFetch<ManagedTenant>(
      `${API_BASE_PATH}/workflows/managed-tenants/${encodeURIComponent(orgId)}`,
      { method: "GET", token: token ?? undefined },
    );
  },

  /** Platform-admin: register an org as a managed tenant. Writes the registry row only
   *  — the control schema is created on the first template switch-on. A duplicate
   *  short name / tenant is a 409 naming the column. */
  async registerManagedTenant(
    body: RegisterManagedTenantRequest,
    token: string | null | undefined,
  ): Promise<ManagedTenant> {
    return portalFetch<ManagedTenant>(`${API_BASE_PATH}/workflows/managed-tenants`, {
      method: "POST",
      token: token ?? undefined,
      body,
    });
  },

  /** Read-only, platform-admin: one org's integrations (in no promised order).
   *  Rejects with a 404 `PortalApiError` when the org is not registered. */
  async listManagedTenantIntegrations(
    orgId: string,
    token: string | null | undefined,
  ): Promise<ManagedTenantIntegration[]> {
    const res = await portalFetch<{ integrations: ManagedTenantIntegration[] }>(
      `${API_BASE_PATH}/workflows/managed-tenants/${encodeURIComponent(orgId)}/integrations`,
      { method: "GET", token: token ?? undefined },
    );
    return res.integrations;
  },

  /** Platform-admin: register a further (non-default) integration. Writes the registry
   *  row only; a taken key / schema / folder is a 409 naming the column. */
  async registerManagedTenantIntegration(
    orgId: string,
    body: RegisterIntegrationRequest,
    token: string | null | undefined,
  ): Promise<ManagedTenantIntegration> {
    return portalFetch<ManagedTenantIntegration>(
      `${API_BASE_PATH}/workflows/managed-tenants/${encodeURIComponent(orgId)}/integrations`,
      { method: "POST", token: token ?? undefined, body },
    );
  },

  /** Read-only, platform-admin: the views in one schema on a connector's database. */
  async listConnectorViews(
    connectorKey: string,
    opts: { direction?: "source" | "target"; schema: string },
    token: string | null | undefined,
  ): Promise<string[]> {
    const qs = new URLSearchParams({ direction: opts.direction ?? "target", schema: opts.schema });
    const res = await portalFetch<{ views: string[] }>(
      `${API_BASE_PATH}/workflows/connectors/${encodeURIComponent(connectorKey)}/views?${qs}`,
      { method: "GET", token: token ?? undefined },
    );
    return res.views;
  },

  /** Read-only, platform-admin: ACTIVE contacts in a Xero mirror whose name contains
   *  `q` (the fee-mapping Contact picker). dp reads the connector's own `target.schema`
   *  — the portal never names a schema. */
  async listXeroContacts(
    connectorKey: string,
    opts: { q: string },
    token: string | null | undefined,
  ): Promise<XeroMirrorList<XeroMirrorContact>> {
    const qs = new URLSearchParams({ q: opts.q });
    return portalFetch<XeroMirrorList<XeroMirrorContact>>(
      `${API_BASE_PATH}/workflows/connectors/${encodeURIComponent(connectorKey)}/xero-contacts?${qs}`,
      { method: "GET", token: token ?? undefined },
    );
  },

  /** Read-only, platform-admin: ACTIVE GL accounts in a Xero mirror whose code starts
   *  with or name contains `q` (the fee-mapping GL account picker). */
  async listXeroAccounts(
    connectorKey: string,
    opts: { q: string },
    token: string | null | undefined,
  ): Promise<XeroMirrorList<XeroMirrorAccount>> {
    const qs = new URLSearchParams({ q: opts.q });
    return portalFetch<XeroMirrorList<XeroMirrorAccount>>(
      `${API_BASE_PATH}/workflows/connectors/${encodeURIComponent(connectorKey)}/xero-accounts?${qs}`,
      { method: "GET", token: token ?? undefined },
    );
  },

  /** Read-only, platform-admin: ACTIVE tax rates in a Xero mirror whose TaxType starts
   *  with or name contains `q` (the credit-note Xero tax type picker). */
  async listXeroTaxRates(
    connectorKey: string,
    opts: { q: string },
    token: string | null | undefined,
  ): Promise<XeroMirrorList<XeroMirrorTaxRate>> {
    const qs = new URLSearchParams({ q: opts.q });
    return portalFetch<XeroMirrorList<XeroMirrorTaxRate>>(
      `${API_BASE_PATH}/workflows/connectors/${encodeURIComponent(connectorKey)}/xero-tax-rates?${qs}`,
      { method: "GET", token: token ?? undefined },
    );
  },

  /** CANONICAL + CUSTOMER-DB WRITE, platform-admin: switch a template on for an
   *  org. Creates the workflow DISABLED, writes its canonical views/tables and
   *  applies them live. Re-sending the same body after a failure resumes; different
   *  inputs replace the instance while it is still disabled. */
  /** Platform-admin, read-only: the inputs and config the org's existing instance of a
   *  template was switched on with. Rejects with a 404 `PortalApiError` when the org has
   *  never switched it on (in that integration). */
  async getCurrentTemplateInstance(
    templateKey: string,
    orgId: string,
    integrationKey: string | undefined,
    token: string | null | undefined,
    signal?: AbortSignal,
  ): Promise<CurrentTemplateInstance> {
    const params = new URLSearchParams({ org_id: orgId });
    if (integrationKey !== undefined) params.set("integration_key", integrationKey);
    return portalFetch<CurrentTemplateInstance>(
      `${API_BASE_PATH}/workflows/templates/${encodeURIComponent(templateKey)}/instances/current?${params}`,
      { method: "GET", token: token ?? undefined, signal },
    );
  },

  async instantiateWorkflowTemplate(
    templateKey: string,
    body: InstantiateWorkflowTemplateRequest,
    token: string | null | undefined,
  ): Promise<InstantiateWorkflowTemplateResponse> {
    return portalFetch<InstantiateWorkflowTemplateResponse>(
      `${API_BASE_PATH}/workflows/templates/${encodeURIComponent(templateKey)}/instances`,
      { method: "POST", token: token ?? undefined, body },
    );
  },
};


// ===========================================================================
// Retry failures
// ===========================================================================

/** One row from ``dp_business2cloud.RetryFailure`` — an exhausted retry
 * attempt that needs operator attention. Written by workflow node primitives
 * (b2c_write, b2c_read, blob_upload, messaging_send) when their retry budget
 * is exhausted; rendered by /portal/workflows/retry-failures. */
export interface RetryFailureResponse {
  id: string;
  organisation_id: string;
  service_name: string;
  operation_id: string;
  operation_kind: string;
  attempt_count: number;
  last_error: string;
  context_json: string | null;
  occurred_at_utc: string;
  resolved_at_utc: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
}

/** Server-sortable columns on ``GET /retry-failures/grid`` — mirrors the
 *  backend ``RetryFailureWriter.SORT_COLUMNS`` allow-list. */
export type RetryFailureSortField =
  | "occurred_at"
  | "service_name"
  | "operation_kind"
  | "attempt_count"
  | "resolved_at";

/** Which rows the grid shows: the open queue (default), resolved, or all. */
export type RetryFailureResolvedFilter = "unresolved" | "resolved" | "all";

/** Filter options for the grid's Selects — DISTINCT over the org and never
 *  narrowed by the current search/filters, so the options stay stable while
 *  filtering. */
export interface RetryFailureFacets {
  service_names: string[];
  operation_kinds: string[];
}

export interface RetryFailureGridResponse {
  rows: RetryFailureResponse[];
  total: number;
  limit: number;
  offset: number;
  facets: RetryFailureFacets;
}

export const retryFailuresApi = {
  /** Retry-failures page grid — paged, searched, sorted and filtered
   *  server-side. ``resolved`` defaults to ``unresolved`` on the backend when
   *  omitted. Unlike ``listUnresolved`` this emits tz-aware UTC datetimes. */
  async listGrid(
    organisationId: string,
    token: string | undefined,
    options?: {
      search?: string;
      serviceName?: string;
      operationKind?: string;
      resolved?: RetryFailureResolvedFilter;
      sortBy?: RetryFailureSortField;
      sortOrder?: "asc" | "desc";
      limit?: number;
      offset?: number;
    },
  ): Promise<RetryFailureGridResponse> {
    const params = new URLSearchParams({ organisation_id: organisationId });
    if (options?.search) params.set("search", options.search);
    if (options?.serviceName) params.set("service_name", options.serviceName);
    if (options?.operationKind) {
      params.set("operation_kind", options.operationKind);
    }
    if (options?.resolved) params.set("resolved", options.resolved);
    if (options?.sortBy) params.set("sort_by", options.sortBy);
    if (options?.sortOrder) params.set("sort_order", options.sortOrder);
    if (options?.limit !== undefined) params.set("limit", String(options.limit));
    if (options?.offset !== undefined) params.set("offset", String(options.offset));
    return portalFetch<RetryFailureGridResponse>(
      `${B2C_BASE_PATH}/retry-failures/grid?${params.toString()}`,
      { method: "GET", token },
    );
  },

  async listUnresolved(
    organisationId: string,
    token: string | undefined,
    options?: { limit?: number },
  ): Promise<RetryFailureResponse[]> {
    const params = new URLSearchParams({ organisation_id: organisationId });
    if (options?.limit !== undefined) {
      params.set("limit", String(options.limit));
    }
    return portalFetch<RetryFailureResponse[]>(
      `${B2C_BASE_PATH}/retry-failures/?${params.toString()}`,
      { method: "GET", token },
    );
  },

  async resolve(
    failureId: string,
    resolutionNote: string,
    token: string | undefined,
  ): Promise<void> {
    await portalFetch(
      `${B2C_BASE_PATH}/retry-failures/${encodeURIComponent(failureId)}/resolve`,
      {
        method: "POST",
        token,
        body: { resolution_note: resolutionNote },
      },
    );
  },
};

// ---------------------------------------------------------------------------
// Schema-provisioning (drift detection + apply) — PLATFORM ADMIN ONLY.
// Backs the /portal/platform/schema-drift page. The /apply call is a
// customer-DB WRITE; the backend gates every call on the platform_admin role
// (X-Platform-User-Is-Admin, edge-stripped so unforgeable) and audits the
// operator. See lara-data-plane src/workflows/api/v2/schema_provisioning.py.
// ---------------------------------------------------------------------------

// Views: ok/drift/missing/error. Tables (as_built): ok/additive-drift/
// destructive-drift/missing — structural drift is tier-classified.
export type SchemaObjectStatus =
  | "ok"
  | "drift"
  | "missing"
  | "error"
  | "additive-drift"
  | "destructive-drift";

export type SchemaObjectKind = "view" | "table";

/** One canonical object's latest sweep verdict for a target. */
export interface SchemaDriftObject {
  object_name: string;
  object_type?: SchemaObjectKind; // view | table (defaults to view for older rows)
  status: SchemaObjectStatus;
  /** Tables only: JSON of the classified structural changes (views leave it null). */
  field_diff?: string | null;
}

/** A discovered provisioning target (tenant + connector + control schema). */
export interface SchemaDriftTarget {
  tenant: string;
  connector_key: string;
  control_schema: string;
  org_id: string;
  objects: SchemaDriftObject[];
  // status→count; may include the table statuses, so key by the status union.
  counts: Partial<Record<SchemaObjectStatus, number>>;
  has_drift: boolean;
  /** False → the sweep hasn't produced a result for this target yet. */
  swept: boolean;
  /** ISO-8601 of the newest verdict for this target (null if never swept). Lets the
   *  UI show freshness so a stale "in sync" is never mistaken for a live one. */
  swept_at?: string | null;
  /** "dsl" — a live workflow writes this schema; "declared" — a managed tenant's
   *  registry row declares it (a tenant with no workflows, e.g. a connector mirror's
   *  views). Optional: a backend older than dp migration 042 does not send it. */
  origin?: "dsl" | "declared";
}

/** A control schema referenced by a workflow with no canonical folder. */
export interface SchemaUnmanaged {
  control_schema: string;
  org_id: string | null;
  workflow_key: string | null;
}

/** A canonical folder no live workflow references. */
export interface SchemaOrphaned {
  control_schema: string;
  tenant: string | null;
}

/** A managed tenant that declares a drift connector but whose canonical folder has no
 *  views yet — nothing can be swept or applied for it until its first view is written. */
export interface SchemaDeclaredGap {
  tenant: string;
  org_id: string;
  connector_key: string;
}

export interface SchemaDriftReport {
  targets: SchemaDriftTarget[];
  unmanaged: SchemaUnmanaged[];
  orphaned: SchemaOrphaned[];
  /** Uncapped counts — the lists above are capped server-side (`_MAX_GAPS`). */
  unmanaged_total?: number;
  orphaned_total?: number;
  /** Optional: a backend older than dp migration 042 does not send these. The list is
   *  capped server-side; the total is the uncapped count. */
  declared_without_canonical?: SchemaDeclaredGap[];
  declared_without_canonical_total?: number;
  /** Non-null ⇒ the ManagedTenant registry could not be read on the server, so the
   *  registry-declared targets are MISSING from `targets` (not absent). */
  declared_unavailable?: string | null;
  /** Only on the /sweep response: true → a scheduled/concurrent sweep held the lock, so
   *  this fire persisted nothing and the report may be stale (don't present it as fresh). */
  sweep_skipped?: boolean;
}

/** The subset of a target the preview/apply endpoints require. */
export interface SchemaTargetRef {
  tenant: string;
  connector_key: string;
  control_schema: string;
  org_id: string;
}

export interface SchemaPreviewPlan {
  object_name: string;
  action: string;
  status: string;
  /** The canonical definition an apply would write. Present for non-ok objects. */
  canonical_ddl?: string | null;
  /** The live definition an apply would overwrite (null when absent live = missing). */
  live_ddl?: string | null;
}

export interface SchemaPreviewResponse {
  tenant: string;
  control_schema: string;
  plans: SchemaPreviewPlan[];
  to_apply: string[];
  blocked: string[];
  /** dp #2383: canonical view files git holds for this tenant that blob does not.
   *  The plan reads blob only, so these are invisible to it — never "in sync".
   *  Optional: absent on a backend that predates the field. */
  git_only?: string[];
}

export interface SchemaApplyResponse {
  tenant: string;
  control_schema: string;
  attempted: string[];
  applied: string[];
  blocked: string[];
  post_sweep_ok: boolean;
}

/** One classified structural change on a table (from the comparator). */
export interface SchemaTableFieldChange {
  kind: string; // e.g. column_missing_live, column_changed, index_missing_live
  detail: string;
  severity: string; // additive | destructive
}

/** One canonical TABLE's plan. ``action``: skip (ok) | create (missing) | add
 * (additive repair — ALTER ADD / CREATE INDEX) | blocked (destructive drift or an
 * unsafe additive change — ``reason`` says why). */
export interface SchemaTablePlan {
  object_name: string;
  action: string;
  status: string;
  reason?: string | null;
  /** The classified structural changes an apply would make (or that block it). */
  field_diff?: SchemaTableFieldChange[];
}

export interface SchemaTablePreviewResponse {
  tenant: string;
  control_schema: string;
  plans: SchemaTablePlan[];
  to_create: string[];
  to_add: string[];
  blocked: string[];
  /** dp #2383: canonical table files in git that blob does not hold. */
  git_only?: string[];
}

export interface SchemaTableApplyResponse {
  tenant: string;
  control_schema: string;
  attempted: string[];
  created: string[];
  added: string[];
  blocked: string[];
  post_sweep_ok: boolean;
}

// ─── Workflow-definition drift (Phase 2b) ────────────────────────────────────
// Separate surface from schema (view/table) drift: canonical-vs-live status of
// the Larasoft-authored workflow DSLs, from the same 6-hourly sweep but scoped to
// ObjectType='workflow'. Namespaced wf_* statuses never feed the schema CRITICAL page.

/** One workflow's canonical-vs-live verdict. */
export type WorkflowDriftStatus =
  | "wf_ok"
  | "wf_drift"
  | "wf_missing_git"
  | "wf_missing_live"
  | "wf_error";

export interface WorkflowDriftItem {
  workflow_key: string;
  status: WorkflowDriftStatus;
}

/** Per managed tenant: its workflows + how many diverge from canonical. */
export interface WorkflowDriftTenant {
  tenant: string;
  workflows: WorkflowDriftItem[];
  /** Count of workflows whose status is not wf_ok. */
  out_of_sync: number;
  /** False → this tenant has no sweep verdict yet (never appeared in a sweep). */
  swept: boolean;
  /** ISO-8601 (UTC offset) of this tenant's newest verdict, or null. Own freshness —
   *  a stale tenant must not look fresh under a globally-advancing last_swept. */
  swept_at?: string | null;
}

export interface WorkflowDriftReport {
  tenants: WorkflowDriftTenant[];
  /** ISO-8601 (UTC offset) of the newest verdict across all tenants, or null. */
  last_swept?: string | null;
}

/** Result of restoring one canonical workflow as a new active version. */
export interface WorkflowRestoreResponse {
  tenant: string;
  workflow_key: string;
  version: number;
  is_enabled: boolean;
  reconcile?: unknown;
  /** The workflow's drift verdict re-checked server-side right after the restore
   *  (canonical vs the new live version, same computation as the sweep). `null` when
   *  the re-check could not run — the restore itself still committed. Absent from a
   *  backend that predates the re-check. */
  drift_status?: WorkflowDriftStatus | null;
  /** False when a running sweep held the tenant's workflow scope, so the verdict was
   *  computed but the stored row is whatever that sweep writes. */
  drift_persisted?: boolean;
}

// ─── Canonical store (W3/W3.1) ───────────────────────────────────────────────
// The tenant canonical (views / tables / workflow DSLs) lives in a VERSIONED blob
// container, not in the DP image. These routes read it, write a new version, and
// apply that version live in one call — see the write warnings on putCanonical.

export type CanonicalKind = "views" | "tables" | "workflows";

/** One blob version of one canonical file. Newest first from the API. */
export interface CanonicalVersion {
  version_id: string;
  is_current: boolean;
  size: number;
  last_modified: string | null;
}

export interface CanonicalFileEntry {
  kind: CanonicalKind;
  name: string;
  size: number;
  /** Null when the manifest has no version for the file (the directory seam keeps none). */
  version_id?: string | null;
  /** The control schema a `views` file declares, read exactly as the backend's
   *  `_tenant_schemas` reads it: `*.sql` only, null when non-.sql, undecodable or with no
   *  `CREATE VIEW [schema].` header. Always null for tables and workflows. */
  schema?: string | null;
}

export interface CanonicalFileList {
  tenant: string;
  /** "package" or "blob" — which store the service is reading (CANONICAL_SOURCE). */
  source: string;
  control_schema: string | null;
  files: CanonicalFileEntry[];
}

export interface CanonicalFileDetail {
  tenant: string;
  kind: CanonicalKind;
  name: string;
  path: string;
  source: string;
  text: string;
  version_id?: string | null;
  versions: CanonicalVersion[];
}

/** The outcome of a canonical write (PUT or restore-version): the blob version that
 *  was written, plus what the immediate live apply did on each discovered target. */
export interface CanonicalWriteResponse {
  tenant: string;
  kind: CanonicalKind;
  name: string;
  version_id: string;
  applied: string[];
  blocked: string[];
  post_sweep_ok: boolean;
  targets: { connector_key: string; org_id: string }[];
  /** workflows kind only. */
  workflow_version?: number;
  reconcile?: unknown;
}

export interface CanonicalParityReport {
  /** Files in the git MIRROR. Still spelled `package_files` because that is the wire
   *  key the API sends: connector-templating W6 kept it deliberately rather than
   *  blanking this count during the window between the DP and website deploys. */
  package_files: number;
  blob_files: number;
  equal: string[];
  differing: string[];
  missing: string[];
  extra: string[];
  in_parity: boolean;
}

// ─── Provider-spec drift (W4.9) ──────────────────────────────────────────────
// Per b2c connector TYPE, the spec-in-blob vs live-metadata verdict AS OF THE LAST
// ESTATE SWEEP. Namespaced spec_* statuses — they never mix with the view/table or
// wf_* rows even though all three share one result table.

export type SpecDriftStatus =
  | "spec_ok"
  | "spec_drift"
  | "spec_unspecced"
  | "spec_missing_live"
  | "spec_error";

export interface SpecDriftType {
  type: string;
  /** Null for spec_missing_live (a spec whose type no longer exists live). */
  connector_type_id: number | null;
  status: SpecDriftStatus;
  spec_version_id?: string | null;
  change_count?: number | null;
  /** change-kind → count, as the sweep recorded it. */
  counts?: Record<string, number> | null;
  warnings: string[];
  /** Class name only — the sweep never echoes driver text to this surface. */
  error?: string | null;
}

export interface SpecDriftReport {
  last_swept: string | null;
  /** False → the spec pass has never run. An empty types[] cannot say that alone. */
  swept: boolean;
  summary: Partial<Record<SpecDriftStatus, number>>;
  out_of_sync: number;
  types: SpecDriftType[];
}

export const schemaProvisioningApi = {
  /** Read-only: discovered targets + their latest per-object drift status (cached —
   *  reflects the last scheduled sweep, up to the sweep interval stale). */
  async getDrift(token: string | null | undefined): Promise<SchemaDriftReport> {
    return portalFetch<SchemaDriftReport>(
      `${API_BASE_PATH}/workflows/schema/drift`,
      { method: "GET", token: token ?? undefined },
    );
  },

  /** Re-run the sweep NOW (recompute live against each tenant DB), then return the
   *  fresh report. This is what "Refresh" calls — so the list can never show a stale
   *  "in sync" after a canonical change. Read-only against tenant DBs. */
  async resweep(token: string | null | undefined): Promise<SchemaDriftReport> {
    return portalFetch<SchemaDriftReport>(
      `${API_BASE_PATH}/workflows/schema/sweep`,
      { method: "POST", token: token ?? undefined },
    );
  },

  /** Read-only: per-object plan (skip / apply / blocked) for a target. */
  async preview(
    target: SchemaTargetRef,
    token: string | null | undefined,
  ): Promise<SchemaPreviewResponse> {
    return portalFetch<SchemaPreviewResponse>(
      `${API_BASE_PATH}/workflows/schema/preview`,
      { method: "POST", token: token ?? undefined, body: target },
    );
  },

  /** CUSTOMER-DB WRITE: apply drifted/missing canonical views to the tenant DB. */
  async apply(
    target: SchemaTargetRef,
    token: string | null | undefined,
  ): Promise<SchemaApplyResponse> {
    return portalFetch<SchemaApplyResponse>(
      `${API_BASE_PATH}/workflows/schema/apply`,
      { method: "POST", token: token ?? undefined, body: target },
    );
  },

  /** Read-only: per-TABLE plan (skip / create / add / blocked) for a target. */
  async previewTables(
    target: SchemaTargetRef,
    token: string | null | undefined,
  ): Promise<SchemaTablePreviewResponse> {
    return portalFetch<SchemaTablePreviewResponse>(
      `${API_BASE_PATH}/workflows/schema/tables/preview`,
      { method: "POST", token: token ?? undefined, body: target },
    );
  },

  /** CUSTOMER-DB WRITE: create missing + additively repair canonical tables. */
  async applyTables(
    target: SchemaTargetRef,
    token: string | null | undefined,
  ): Promise<SchemaTableApplyResponse> {
    return portalFetch<SchemaTableApplyResponse>(
      `${API_BASE_PATH}/workflows/schema/tables/apply`,
      { method: "POST", token: token ?? undefined, body: target },
    );
  },

  /** Read-only: canonical-vs-live drift of Larasoft-authored workflow DEFINITIONS per
   *  managed tenant (ObjectType='workflow', wf_* statuses). From the same sweep. */
  async getWorkflowDrift(
    token: string | null | undefined,
  ): Promise<WorkflowDriftReport> {
    return portalFetch<WorkflowDriftReport>(
      `${API_BASE_PATH}/workflows/schema/workflows/drift`,
      { method: "GET", token: token ?? undefined },
    );
  },

  /** PROD WRITE: republish a canonical workflow definition as a new active
   *  version. Routes through the full publish safety stack (validate / normalise-org
   *  / existence-guard / reconcile) — a deleted key 404s, it never resurrects. */
  async restoreWorkflow(
    tenant: string,
    workflowKey: string,
    token: string | null | undefined,
  ): Promise<WorkflowRestoreResponse> {
    return portalFetch<WorkflowRestoreResponse>(
      `${API_BASE_PATH}/workflows/schema/workflows/restore`,
      {
        method: "POST",
        token: token ?? undefined,
        body: { tenant, workflow_key: workflowKey },
      },
    );
  },

  /** Read-only: the swept provider-spec verdict per b2c connector type (W4.9).
   *  Reflects the last ESTATE sweep — a scoped re-check does not refresh it. */
  async getSpecDrift(
    token: string | null | undefined,
  ): Promise<SpecDriftReport> {
    return portalFetch<SpecDriftReport>(
      `${API_BASE_PATH}/workflows/schema/specs/drift`,
      { method: "GET", token: token ?? undefined },
    );
  },

  /** Read-only: a tenant's canonical files with their current blob version ids. */
  async listCanonical(
    tenant: string,
    token: string | null | undefined,
  ): Promise<CanonicalFileList> {
    return portalFetch<CanonicalFileList>(
      `${API_BASE_PATH}/workflows/schema/canonical/${encodeURIComponent(tenant)}`,
      { method: "GET", token: token ?? undefined },
    );
  },

  /** Read-only: one canonical file's current text + its version history. */
  async getCanonical(
    tenant: string,
    kind: CanonicalKind,
    name: string,
    token: string | null | undefined,
  ): Promise<CanonicalFileDetail> {
    return portalFetch<CanonicalFileDetail>(
      `${API_BASE_PATH}/workflows/schema/canonical/${encodeURIComponent(tenant)}/${kind}/${encodeURIComponent(name)}`,
      { method: "GET", token: token ?? undefined },
    );
  },

  /** CANONICAL + CUSTOMER-DB WRITE. Validate → write a new blob version → apply it
   *  live, in that order, and NOTHING is rolled back: a failed apply still leaves the
   *  new version written (the error body carries `version_id`), so the canonical is
   *  ahead of live until restoreCanonicalVersion undoes it. `expectedVersionId` is the
   *  version the editor loaded — null means "I expect no file yet"; a mismatch is 409. */
  async putCanonical(
    tenant: string,
    kind: CanonicalKind,
    name: string,
    body: { text: string; expected_version_id: string | null },
    token: string | null | undefined,
  ): Promise<CanonicalWriteResponse> {
    return portalFetch<CanonicalWriteResponse>(
      `${API_BASE_PATH}/workflows/schema/canonical/${encodeURIComponent(tenant)}/${kind}/${encodeURIComponent(name)}`,
      { method: "PUT", token: token ?? undefined, body },
    );
  },

  /** CANONICAL + CUSTOMER-DB WRITE — the undo for a bad put. Re-publishes older bytes
   *  as a NEW current version (history is never rewritten) and applies them like a put. */
  async restoreCanonicalVersion(
    tenant: string,
    kind: CanonicalKind,
    name: string,
    versionId: string,
    token: string | null | undefined,
  ): Promise<CanonicalWriteResponse> {
    return portalFetch<CanonicalWriteResponse>(
      `${API_BASE_PATH}/workflows/schema/canonical/${encodeURIComponent(tenant)}/${kind}/${encodeURIComponent(name)}/restore-version`,
      { method: "POST", token: token ?? undefined, body: { version_id: versionId } },
    );
  },

  /** Read-only: byte-exact package-vs-blob comparison. `in_parity` must be true
   *  before the CANONICAL_SOURCE=blob cutover, and is the standing check that the
   *  git mirror is faithful afterwards. */
  async canonicalParity(
    token: string | null | undefined,
  ): Promise<CanonicalParityReport> {
    return portalFetch<CanonicalParityReport>(
      `${API_BASE_PATH}/workflows/schema/canonical/parity`,
      { method: "GET", token: token ?? undefined },
    );
  },
};
